/**
 * Klaviyo behavioral events, ported from the legacy theme (BRD-420).
 *
 * Replicates the legacy `web_remove_from_cart` Klaviyo event so abandoned-cart
 * flows and the marketing agency's reports keep working unchanged after the
 * Horizon move. Requires the Klaviyo onsite embed (window.klaviyo), live on prod.
 *
 * Safety: this only listens to the `cart:update` document event and calls
 * Klaviyo. It never mutates the cart, intercepts fetch, or blocks Horizon's
 * handlers, so it cannot affect the commerce flow.
 *
 * Approach: Horizon's `cart:update` exposes the cart AFTER a change, so we keep
 * a snapshot of the previous cart and diff quantities to detect removals. The
 * baseline is seeded synchronously from window.SHOPIFY_INITIAL_CART (rendered
 * inline) so the first removal is never missed. Add events ship an empty
 * resource and are ignored.
 *
 * Two deliberate divergences from legacy, documented in
 * docs/research/prod-tracking-inventory.md:
 *  - every removal sends the full payload (Horizon cannot distinguish a
 *    minus-to-zero from a trash-icon removal at the event layer).
 *  - item fields come from the removed line, not the product default variant.
 */
(function () {
  if (window.__klaviyoCartEvents) return;
  window.__klaviyoCartEvents = true;

  function track(eventName, properties) {
    // Consent gate (BRD-422): marketing consent, fail-closed.
    if (!window.tbConsent || !window.tbConsent.marketing()) return;
    try {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(eventName, properties);
      } else if (Array.isArray(window._learnq)) {
        window._learnq.push(['track', eventName, properties]);
      }
    } catch (error) {
      /* tracking must never break the cart */
    }
  }

  function snapshot(items) {
    const map = new Map();
    (items || []).forEach((item) => map.set(item.key || String(item.id), item));
    return map;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
  }

  // Seed synchronously from the cart rendered into the page so the first removal has a baseline.
  let previousItems = snapshot(window.SHOPIFY_INITIAL_CART && window.SHOPIFY_INITIAL_CART.items);

  // Diff the previous cart against the next one and fire web_remove_from_cart for any decrease.
  function reconcile(items, currencyFromEvent) {
    const next = snapshot(items);
    const email = window.SHOPIFY_CUSTOMER_EMAIL || '';
    // Active presentment currency, so multi-currency / multi-market shoppers report what they saw.
    const currency =
      currencyFromEvent || (window.SHOPIFY_INITIAL_CART && window.SHOPIFY_INITIAL_CART.currency) || 'EUR';

    previousItems.forEach((prev, key) => {
      const current = next.get(key);
      const previousQty = prev.quantity || 0;
      const nextQty = current ? current.quantity || 0 : 0;
      if (nextQty >= previousQty) return;

      if (nextQty === 0) {
        // Full line removal, mirrors the legacy full payload.
        track('web_remove_from_cart', {
          email,
          currency,
          item_brand: prev.vendor || '',
          item_name: prev.product_title || prev.title || '',
          item_id: prev.id,
          item_price: (prev.price || 0) / 100,
          item_url: `${window.location.origin}${prev.url || ''}`,
        });
      } else {
        // Quantity decrement, mirrors the legacy minimal payload.
        track('web_remove_from_cart', { email, currency, item_id: prev.id });
      }
    });

    previousItems = next;
  }

  let cartEventSeq = 0;
  document.addEventListener('cart:update', (event) => {
    const seq = ++cartEventSeq;
    const resource = event?.detail?.resource;
    if (resource && Array.isArray(resource.items)) {
      // Full cart payload (removal or quantity change): diff synchronously.
      reconcile(resource.items, resource.currency);
    } else {
      // Add (or other) event ships an empty resource. No removal to detect, but refresh the
      // baseline from the cart so a later in-session removal diffs correctly. Drop the result
      // if a newer cart event arrived meanwhile, so a stale fetch can't overwrite fresher state.
      fetchCart()
        .then((cart) => {
          if (seq === cartEventSeq) reconcile(cart.items, cart.currency);
        })
        .catch(() => {});
    }
  });
})();
